import React, { useState, useRef, useEffect, useId } from 'react';
import './FormSelect.css';

/**
 * FormSelect component with ArbiWin design system integration
 * Custom dropdown select with enhanced styling and functionality
 *
 * @param {Object} props - FormSelect properties
 * @param {Array} props.options - Array of option objects {value, label, disabled}
 * @param {string} props.value - Selected value
 * @param {string} props.defaultValue - Default selected value
 * @param {Function} props.onChange - Change handler
 * @param {Function} props.onBlur - Blur handler
 * @param {Function} props.onFocus - Focus handler
 * @param {string} props.name - Field name
 * @param {string} props.label - Field label
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.required - Mark as required
 * @param {boolean} props.disabled - Disable select
 * @param {boolean} props.multiple - Allow multiple selections
 * @param {string} props.error - Error message
 * @param {string} props.helperText - Helper text
 * @param {string} props.size - Select size: 'small', 'medium', 'large'
 * @param {boolean} props.fullWidth - Make select full width
 * @param {boolean} props.searchable - Enable search functionality
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.testId - Test ID for testing
 * @returns {JSX.Element} FormSelect component
 */
const FormSelect = function FormSelect({
  options = [],
  value,
  defaultValue,
  onChange,
  onBlur,
  onFocus,
  name,
  label,
  placeholder = 'Изберете опция...',
  required = false,
  disabled = false,
  multiple = false,
  error,
  helperText,
  size = 'large',
  fullWidth = true,
  searchable = false,
  className = '',
  testId,
  ...rest
}) {

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const selectRef = useRef(null);
  const listRef = useRef(null);
  const searchInputRef = useRef(null);
  const selectId = useId();
  const errorId = useId();
  const helperTextId = useId();

  // Filter options based on search term
  const filteredOptions = searchable && searchTerm
    ? options.filter(function(option) {
      return option.label.toLowerCase().includes(searchTerm.toLowerCase());
    })
    : options;

  // Get selected option(s)
  const selectedOption = multiple
    ? options.filter(function(option) {
      return Array.isArray(value) ? value.includes(option.value) : false;
    })
    : options.find(function(option) {
      return option.value === value;
    });

  const hasError = Boolean(error);
  const shouldShowHelperText = Boolean(helperText && !hasError);
  const shouldShowError = Boolean(error);

  const selectClasses = [
    'arbiwin-form-select',
    'arbiwin-form-select--' + size,
    isOpen ? 'arbiwin-form-select--open' : '',
    hasError ? 'arbiwin-form-select--error' : '',
    disabled ? 'arbiwin-form-select--disabled' : '',
    fullWidth ? 'arbiwin-form-select--full-width' : '',
    multiple ? 'arbiwin-form-select--multiple' : '',
    searchable ? 'arbiwin-form-select--searchable' : '',
    className
  ].filter(Boolean).join(' ');

  // Handle option selection
  const handleOptionSelect = function(option) {
    if (option.disabled) return;

    let newValue;
    if (multiple) {
      const currentValue = Array.isArray(value) ? value : [];
      if (currentValue.includes(option.value)) {
        newValue = currentValue.filter(function(v) { return v !== option.value; });
      } else {
        newValue = currentValue.concat([option.value]);
      }
    } else {
      newValue = option.value;
      setIsOpen(false);
    }

    if (onChange) {
      const event = {
        target: {
          name: name,
          value: newValue
        }
      };
      onChange(event);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = function(event) {
    if (disabled) return;

    switch (event.key) {
      case 'Enter':
      case ' ':
        if (!isOpen) {
          setIsOpen(true);
          setFocusedIndex(0);
        } else if (focusedIndex >= 0 && focusedIndex < filteredOptions.length) {
          handleOptionSelect(filteredOptions[focusedIndex]);
        }
        event.preventDefault();
        break;

      case 'Escape':
        setIsOpen(false);
        setFocusedIndex(-1);
        selectRef.current?.focus();
        break;

      case 'ArrowDown':
        if (!isOpen) {
          setIsOpen(true);
          setFocusedIndex(0);
        } else {
          setFocusedIndex(function(prev) {
            return Math.min(prev + 1, filteredOptions.length - 1);
          });
        }
        event.preventDefault();
        break;

      case 'ArrowUp':
        if (isOpen) {
          setFocusedIndex(function(prev) {
            return Math.max(prev - 1, 0);
          });
        }
        event.preventDefault();
        break;

      case 'Tab':
        setIsOpen(false);
        break;

      default:
        if (searchable && isOpen && event.key.length === 1) {
          searchInputRef.current?.focus();
        }
        break;
    }
  };

  // Handle click outside to close
  useEffect(function() {
    const handleClickOutside = function(event) {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return function() {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Handle focus events
  const handleFocus = function() {
    if (onFocus) {
      onFocus();
    }
  };

  const handleBlur = function() {
    if (onBlur) {
      onBlur();
    }
  };

  // Scroll focused option into view
  useEffect(function() {
    if (isOpen && focusedIndex >= 0 && listRef.current) {
      const focusedElement = listRef.current.children[focusedIndex];
      if (focusedElement) {
        focusedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [isOpen, focusedIndex]);

  // Get display text
  const getDisplayText = function() {
    if (multiple && selectedOption && selectedOption.length > 0) {
      if (selectedOption.length === 1) {
        return selectedOption[0].label;
      }
      return selectedOption.length + ' избрани опции';
    }

    if (!multiple && selectedOption) {
      return selectedOption.label;
    }

    return placeholder;
  };

  const describedBy = [];
  if (shouldShowError) describedBy.push(errorId);
  if (shouldShowHelperText) describedBy.push(helperTextId);

  return (
    <div className={selectClasses}>
      {label && (
        <label
          htmlFor={selectId}
          className="arbiwin-form-select__label"
        >
          {label}
          {required && (
            <span className="arbiwin-form-select__required" aria-label="задължително поле">
              *
            </span>
          )}
        </label>
      )}

      <div className="arbiwin-form-select__wrapper">
        <button
          ref={selectRef}
          id={selectId}
          type="button"
          className="arbiwin-form-select__trigger"
          onClick={function() { if (!disabled) setIsOpen(!isOpen); }}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-describedby={describedBy.length > 0 ? describedBy.join(' ') : undefined}
          data-testid={testId || 'arbiwin-form-select'}
          {...rest}
        >
          <span className="arbiwin-form-select__value">
            {getDisplayText()}
          </span>
          <span className="arbiwin-form-select__arrow" aria-hidden="true">
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </span>
        </button>

        {isOpen && (
          <div className="arbiwin-form-select__dropdown">
            {searchable && (
              <div className="arbiwin-form-select__search">
                <input
                  ref={searchInputRef}
                  type="text"
                  className="arbiwin-form-select__search-input"
                  placeholder="Търсене..."
                  value={searchTerm}
                  onChange={function(event) { setSearchTerm(event.target.value); }}
                  onKeyDown={function(event) {
                    if (event.key === 'Escape') {
                      setSearchTerm('');
                      selectRef.current?.focus();
                    }
                  }}
                />
              </div>
            )}

            <ul
              ref={listRef}
              className="arbiwin-form-select__list"
              role="listbox"
              aria-multiselectable={multiple}
            >
              {filteredOptions.length === 0 ? (
                <li className="arbiwin-form-select__no-options">
                  {searchTerm ? 'Няма намерени резултати' : 'Няма налични опции'}
                </li>
              ) : (
                filteredOptions.map(function(option, index) {
                  const isSelected = multiple
                    ? Array.isArray(value) && value.includes(option.value)
                    : value === option.value;
                  const isFocused = index === focusedIndex;

                  return (
                    <li
                      key={option.value}
                      className={[
                        'arbiwin-form-select__option',
                        isSelected ? 'arbiwin-form-select__option--selected' : '',
                        isFocused ? 'arbiwin-form-select__option--focused' : '',
                        option.disabled ? 'arbiwin-form-select__option--disabled' : ''
                      ].filter(Boolean).join(' ')}
                      role="option"
                      aria-selected={isSelected}
                      onClick={function() { handleOptionSelect(option); }}
                      onMouseEnter={function() { setFocusedIndex(index); }}
                    >
                      {multiple && (
                        <span className="arbiwin-form-select__checkbox">
                          {isSelected && (
                            <svg viewBox="0 0 20 20" fill="currentColor">
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                        </span>
                      )}
                      <span className="arbiwin-form-select__option-text">
                        {option.label}
                      </span>
                      {!multiple && isSelected && (
                        <span className="arbiwin-form-select__checkmark">
                          <svg viewBox="0 0 20 20" fill="currentColor">
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </span>
                      )}
                    </li>
                  );
                })
              )}
            </ul>
          </div>
        )}
      </div>

      {shouldShowError && (
        <div
          id={errorId}
          className="arbiwin-form-select__error"
          role="alert"
          aria-live="polite"
        >
          <svg
            className="arbiwin-form-select__error-icon"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          {error}
        </div>
      )}

      {shouldShowHelperText && (
        <div
          id={helperTextId}
          className="arbiwin-form-select__helper-text"
        >
          {helperText}
        </div>
      )}
    </div>
  );
};

FormSelect.displayName = 'FormSelect';

export default FormSelect;
